import React, { createContext, useContext, useMemo } from "react";
import type {
  ResolvedRolePermissions,
  SuperAdminMeIdentity,
} from "@/services/superAdminAccessService";
import {
  canEditPage,
  canViewPage,
  hasPlatformPower,
  resolveSessionRolePermissions,
} from "@/features/employees/employeeRolePermissions";

const SuperAdminIdentityContext = createContext<SuperAdminMeIdentity | null>(
  null,
);

export function SuperAdminIdentityProvider({
  identity,
  children,
}: {
  identity: SuperAdminMeIdentity;
  children: React.ReactNode;
}) {
  return (
    <SuperAdminIdentityContext.Provider value={identity}>
      {children}
    </SuperAdminIdentityContext.Provider>
  );
}

export function useSuperAdminIdentity(): SuperAdminMeIdentity | null {
  return useContext(SuperAdminIdentityContext);
}

export function useResolvedRolePermissions(): ResolvedRolePermissions | null {
  const identity = useSuperAdminIdentity();
  return useMemo(() => resolveSessionRolePermissions(identity), [identity]);
}

export function useCanViewPage(pageId: string): boolean {
  const permissions = useResolvedRolePermissions();
  return useMemo(() => canViewPage(permissions, pageId), [permissions, pageId]);
}

export function useCanEditPage(pageId: string): boolean {
  const permissions = useResolvedRolePermissions();
  return useMemo(() => canEditPage(permissions, pageId), [permissions, pageId]);
}

export function useHasPlatformPower(powerId: string): boolean {
  const permissions = useResolvedRolePermissions();
  return useMemo(
    () => hasPlatformPower(permissions, powerId),
    [permissions, powerId],
  );
}

/** True when the signed-in user passed the Super Admin allow-list probe. */
export function useIsSuperAdmin(): boolean {
  const identity = useSuperAdminIdentity();
  return identity?.isSuperAdmin === true;
}

/**
 * Platform-wide delete/archive/restore/purge authority (Super Admin role only).
 * Do not use identity.isSuperAdmin — that flag means "active SA employee", not
 * the super-admin role. Edit page permission does not imply lifecycle delete.
 */
export function useCanPerformLifecycleDelete(): boolean {
  const identity = useSuperAdminIdentity();
  const permissions = useResolvedRolePermissions();
  return useMemo(() => {
    if (!identity) return false;
    if (identity.employeeRoleKey === "super-admin") return true;
    if (permissions?.isFullAccess === true) return true;
    return false;
  }, [identity, permissions]);
}

/** True when the signed-in employee may edit employee records. */
export function useCanManageEmployees(): boolean {
  return useCanEditPage("employees");
}
